import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { ArrowLeft, X } from 'lucide-react';
import { useGetFeedQuery, PostResponseSchema } from '@api/client';
import { createStyledMap } from '../../share/utils/mapStyling';
import PostCard from '@feature/post-item/PostCard';
import IntroAnimation from './IntroAnimation';
import { MAP_ZOOM_LEVELS } from '../constants/mapConstants';
import { VideoPlayerModal } from '@feature/play-video';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../../../App.css';

// Add styles to make mapbox canvas background transparent and hide logo
const style = document.createElement('style');
style.textContent = `
  .mapboxgl-canvas {
    background: transparent !important;
  }
  .mapboxgl-map {
    background: transparent !important;
  }
  .mapboxgl-ctrl-logo {
    display: none !important;
  }
  .mapboxgl-ctrl-attrib {
    display: none !important;
  }
`;
if (!document.head.querySelector('style[data-mapbox-transparent]')) {
  style.setAttribute('data-mapbox-transparent', 'true');
  document.head.appendChild(style);
}

interface HeroGlobeProps {
  isMobile: boolean;
  onZoomChange?: (zoom: number) => void;
  onPostSelect?: (selected: boolean) => void;
  onGlobeLayout?: (leftPosition: number, rightPosition: number, topPosition: number, bottomPosition: number) => void;
  isPhoneFrameMode?: boolean;
  onIntroComplete?: () => void;
}

const SECONDS_PER_REVOLUTION = 60; // Complete revolution every minute

export default function HeroGlobe({ isMobile, onZoomChange, onPostSelect, onGlobeLayout, isPhoneFrameMode = false, onIntroComplete }: HeroGlobeProps): React.ReactElement {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const userInteractingRef = useRef(false);
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [isRotating, setIsRotating] = useState(true);
  const [selectedPost, setSelectedPost] = useState<PostResponseSchema | null>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);

  // Disable body scroll when image modal is open (VideoPlayerModal handles its own scroll)
  useEffect(() => {
    if (isImageModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isImageModalOpen]);

  // Fetch first 2 pages of popular feed posts
  const { data: feedDataPage1, isLoading: isLoadingPage1 } = useGetFeedQuery({
    layerId: 'popular',
    sortBy: 'trending',
    page: 1,
    size: 100,
  });

  const { data: feedDataPage2, isLoading: isLoadingPage2 } = useGetFeedQuery({
    layerId: 'popular',
    sortBy: 'trending',
    page: 2,
    size: 100,
  });

  // Combine both pages
  const feedData = useMemo(() => {
    if (!feedDataPage1 && !feedDataPage2) return null;

    const items = [
      ...(feedDataPage1?.items || []),
      ...(feedDataPage2?.items || [])
    ];

    return {
      items,
      total: feedDataPage1?.total || 0,
      page: 1,
      size: items.length,
      pages: feedDataPage1?.pages || 0
    };
  }, [feedDataPage1, feedDataPage2]);

  const isLoading = isLoadingPage1 || isLoadingPage2;

  // Spin globe function - event-driven rotation
  const spinGlobe = useCallback(() => {
    if (!map.current || !isRotating || userInteractingRef.current) return;

    const center = map.current.getCenter();
    const degreesPerSecond = 360 / SECONDS_PER_REVOLUTION;
    center.lng -= degreesPerSecond;

    // Smoothly animate over 1 second. When complete, 'moveend' event fires
    map.current.easeTo({
      center,
      duration: 1000,
      easing: (n) => n
    });
  }, [isRotating]);

  // Initialize map - only once on mount
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Determine initial mobile state at mount time
    const isInitiallyMobile = window.innerWidth <= 1350;

    // Initialize map with consistent styling - enable zoom controls
    map.current = createStyledMap(mapContainer.current, {
      center: [0, 20],
      zoom: isInitiallyMobile ? MAP_ZOOM_LEVELS.INITIAL_MOBILE : MAP_ZOOM_LEVELS.INITIAL_DESKTOP,
      interactive: true,
      dragPan: true,
      scrollZoom: true,
      doubleClickZoom: true,
      touchZoomRotate: true,
      boxZoom: false,
    });

    // Offset the globe down in the viewport by adding top padding
    map.current.easeTo({
      padding: { top: isInitiallyMobile ? 0 : 15, bottom: 0, left: 0, right: 0 },
      duration: 0 // Instant, no animation
    });

    console.log('[HeroGlobe] Map initialized');

    // Calculate and report globe's left and right edge positions for text centering

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, [onGlobeLayout]); // Removed isMobile from dependencies

  // Calculate text positions based on phone frame position
  useEffect(() => {
    if (isMobile || !onGlobeLayout || !mapContainer.current) return;

    const recalculateLayout = () => {
      if (!mapContainer.current?.parentElement) {
        console.log('[HeroGlobe] recalculateLayout: no parent element');
        return;
      }

      // Get the phone frame container (parent of globe container)
      const phoneFrameContainer = mapContainer.current.parentElement;
      const rect = phoneFrameContainer.getBoundingClientRect();
      const phoneFrameLeftEdge = rect.left;
      const phoneFrameRightEdge = rect.right;
      const viewportWidth = window.innerWidth;

      // Calculate center of space between viewport left edge and phone frame left edge
      const leftTextCenterpoint = phoneFrameLeftEdge / 2;

      // Calculate center of space between phone frame right edge and viewport right edge
      const spaceRight = viewportWidth - phoneFrameRightEdge;
      const rightTextCenterpoint = spaceRight / 2;

      // Not used in phone frame mode, but required by callback
      const topTextCenterpoint = 0;
      const bottomTextCenterpoint = 0;

      console.log('[HeroGlobe] POSITION CALCULATED:', {
        phoneFrameLeftEdge,
        phoneFrameRightEdge,
        viewportWidth,
        leftTextCenterpoint,
        rightTextCenterpoint
      });

      onGlobeLayout(leftTextCenterpoint, rightTextCenterpoint, topTextCenterpoint, bottomTextCenterpoint);
    };

    // Listen to resize events
    window.addEventListener('resize', recalculateLayout);

    // Calculate on mount
    recalculateLayout();

    return () => {
      window.removeEventListener('resize', recalculateLayout);
    };
  }, [isMobile, onGlobeLayout, isPhoneFrameMode]);

  // Reset inactivity timer - only if modal is not open
  const resetInactivityTimer = useCallback(() => {
    // Don't start timer if modal is open
    if (selectedPost) return;

    // Clear existing timer
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    // Only start timer if zoomed in
    if (map.current && map.current.getZoom() > 2) {
      console.log('[HeroGlobe] Starting 5-second inactivity timer');
      inactivityTimeoutRef.current = setTimeout(() => {
        if (map.current && map.current.getZoom() > 2 && !selectedPost) {
          console.log('[HeroGlobe] 5 seconds of inactivity, zooming out');
          const isActuallyMobile = window.innerWidth <= 1350;
          map.current.easeTo({
            center: [map.current.getCenter().lng, 20],
            zoom: isActuallyMobile ? MAP_ZOOM_LEVELS.INITIAL_MOBILE : MAP_ZOOM_LEVELS.INITIAL_DESKTOP,
            pitch: 0, // Reset tilt to bird's eye view
            duration: 1000,
            padding: { top: isActuallyMobile ? 0 : 15, bottom: 0, left: 0, right: 0 }
          });

          // Resume rotation after zoom animation completes
          setTimeout(() => {
            userInteractingRef.current = false;
            spinGlobe();
          }, 1100);
        }
      }, 5000);
    }
  }, [selectedPost, spinGlobe]);

  // Setup event listeners and start rotation
  useEffect(() => {
    if (!map.current) return;

    // Pause rotation on interaction start
    const handleInteractionStart = () => {
      console.log('[HeroGlobe] User interaction started');
      userInteractingRef.current = true;
    };

    // Resume rotation on interaction end
    const handleInteractionEnd = () => {
      console.log('[HeroGlobe] User interaction ended');

      // Notify parent of zoom change
      if (map.current && onZoomChange) {
        onZoomChange(map.current.getZoom());
      }

      // Only resume rotation if at globe zoom level
      if (map.current && map.current.getZoom() <= 2) {
        userInteractingRef.current = false;
        spinGlobe();
      } else {
        // Still zoomed in, reset inactivity timer (only if modal not open)
        resetInactivityTimer();
      }
    };

    // Event listeners for immediate interaction detection
    map.current.on('mousedown', handleInteractionStart);
    map.current.on('touchstart', handleInteractionStart);
    map.current.on('wheel', handleInteractionStart); // Scroll zoom
    map.current.on('dblclick', handleInteractionStart); // Double click zoom

    // Resume rotation when interaction completes
    map.current.on('mouseup', handleInteractionEnd);
    map.current.on('touchend', handleInteractionEnd);
    map.current.on('dragend', handleInteractionEnd);
    map.current.on('pitchend', handleInteractionEnd);
    map.current.on('rotateend', handleInteractionEnd);
    map.current.on('zoomend', handleInteractionEnd);

    // When animation completes, trigger next rotation
    map.current.on('moveend', spinGlobe);

    // Start initial rotation
    spinGlobe();

    return () => {
      if (map.current) {
        map.current.off('mousedown', handleInteractionStart);
        map.current.off('touchstart', handleInteractionStart);
        map.current.off('wheel', handleInteractionStart);
        map.current.off('dblclick', handleInteractionStart);
        map.current.off('mouseup', handleInteractionEnd);
        map.current.off('touchend', handleInteractionEnd);
        map.current.off('dragend', handleInteractionEnd);
        map.current.off('pitchend', handleInteractionEnd);
        map.current.off('rotateend', handleInteractionEnd);
        map.current.off('zoomend', handleInteractionEnd);
        map.current.off('moveend', spinGlobe);
      }
    };
  }, [spinGlobe, resetInactivityTimer]);

  // Close modal - zoom out immediately to initial state
  const closeModal = useCallback(() => {
    // Clear any existing inactivity timeout
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = undefined;
    }

    // Notify parent that post is deselected
    if (onPostSelect) {
      onPostSelect(false);
    }

    // Close modal
    setSelectedPost(null);

    // Zoom out to initial state
    if (map.current) {
      console.log('[HeroGlobe] Modal closed, zooming out to initial state');
      const isActuallyMobile = window.innerWidth <= 1350;
      map.current.easeTo({
        center: [map.current.getCenter().lng, 20],
        zoom: isActuallyMobile ? MAP_ZOOM_LEVELS.INITIAL_MOBILE : MAP_ZOOM_LEVELS.INITIAL_DESKTOP,
        pitch: 0, // Reset tilt to bird's eye view
        duration: 1000,
        padding: { top: isActuallyMobile ? 0 : 15, bottom: 0, left: 0, right: 0 }
      });

      // Resume rotation after zoom animation completes
      setTimeout(() => {
        userInteractingRef.current = false;
        spinGlobe();
      }, 1100);
    }
  }, [spinGlobe, onPostSelect]);

  // Handle marker click - zoom in and show modal (no timer)
  const handleMarkerClick = useCallback((post: PostResponseSchema) => {
    if (!map.current) return;

    // Clear any existing inactivity timeout
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = undefined;
    }

    // Pause rotation
    userInteractingRef.current = true;

    // Notify parent that post is selected
    if (onPostSelect) {
      onPostSelect(true);
    }

    // Zoom in to the marker at street level
    map.current.easeTo({
      center: [post.longitude, post.latitude],
      zoom: MAP_ZOOM_LEVELS.STREET_LEVEL,
      duration: 1000,
      padding: { top: isMobile ? 0 : 15, bottom: 0, left: 0, right: 0 }
    });

    // Show post modal (timer will NOT start while modal is open)
    setSelectedPost(post);
  }, [onPostSelect]);

  // Create custom marker element matching ShareMapFullscreen styling
  const createMarkerElement = (post: PostResponseSchema) => {
    const el = document.createElement('div');
    el.style.cssText = `
      width: 30px;
      height: 40px;
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
    `;

    // Main circle
    const circle = document.createElement('div');
    circle.style.cssText = `
      width: 30px;
      height: 30px;
      background: #fc2c03;
      border-radius: 50%;
      border: 1px solid #fc2c03;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s ease;
    `;

    // Icon (simplified - just use a dot for non-interactive display)
    const icon = document.createElement('div');
    icon.style.cssText = `
      width: 8px;
      height: 8px;
      background: white;
      border-radius: 50%;
    `;

    // Triangle pointer
    const triangle = document.createElement('div');
    triangle.style.cssText = `
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 10px solid #fc2c03;
      margin-top: -5px;
      transition: transform 0.2s ease;
    `;

    circle.appendChild(icon);
    el.appendChild(circle);
    el.appendChild(triangle);

    // Click handler
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      handleMarkerClick(post);
    });

    // Hover effects
    el.addEventListener('mouseenter', () => {
      circle.style.transform = 'scale(1.2)';
      triangle.style.transform = 'scale(1.2)';
    });

    el.addEventListener('mouseleave', () => {
      circle.style.transform = 'scale(1)';
      triangle.style.transform = 'scale(1)';
    });

    return el;
  };

  // Add markers for posts
  useEffect(() => {
    if (!map.current || !feedData?.items) return;

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add new markers - match ShareMapFullscreen styling
    feedData.items.forEach(post => {
      const markerElement = createMarkerElement(post);

      const marker = new mapboxgl.Marker({
        element: markerElement,
        anchor: 'bottom'
      })
        .setLngLat([post.longitude, post.latitude])
        .addTo(map.current!);

      markers.current.push(marker);
    });
  }, [feedData]);

  return (
    <>
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: 'transparent',
      }}>
        {/* Intro Animation */}
        {!introComplete && (
          <IntroAnimation
            onComplete={() => {
              setIntroComplete(true);
              if (onIntroComplete) {
                onIntroComplete();
              }
            }}
            isMobile={isMobile}
          />
        )}

        <div
          ref={mapContainer}
          style={{
            width: '100%',
            height: '100%',
            background: 'transparent',
            opacity: isLoading || !introComplete ? 0 : 1,
            transition: 'opacity 0.2s ease',
          }}
        />

        {/* Loading spinner */}
        {isLoading && introComplete && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
          }}>
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #dc2626',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Post Preview Modal */}
      {selectedPost && !isVideoModalOpen && !isImageModalOpen && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'transparent',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px',
            paddingBottom: '60px',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              borderRadius: '12px',
              maxWidth: isMobile ? '90%' : '400px',
              width: '100%',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Post Card */}
            <PostCard
              post={selectedPost}
              onVideoClick={(post) => {
                setIsVideoModalOpen(true);
              }}
              onImageClick={(post) => {
                setIsImageModalOpen(true);
              }}
              roundedBottom={true}
              compact={true}
              showCloseButton={true}
              onClose={closeModal}
            />
          </div>
        </div>
      )}

      {/* Video Modal */}
      <VideoPlayerModal
        isOpen={isVideoModalOpen && Boolean(selectedPost?.m3u8_url)}
        videoUrl={selectedPost?.m3u8_url || ''}
        onClose={() => {
          setIsVideoModalOpen(false);
          closeModal();
        }}
        playing
        controls
        loop
        isPhoneFrameMode={isPhoneFrameMode}
      />

      {/* Image Modal */}
      {isImageModalOpen && selectedPost?.photo_url && (
        <div
          className="image-modal"
          style={isPhoneFrameMode ? {
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            borderRadius: '45px',
            overflow: 'hidden',
          } : undefined}
          onClick={() => {
            setIsImageModalOpen(false);
            closeModal();
          }}
        >
          <button
            style={{
              position: isPhoneFrameMode ? 'absolute' : 'fixed',
              top: isPhoneFrameMode ? '50px' : '80px',
              right: isPhoneFrameMode ? '20px' : '20px',
              background: 'rgba(0, 0, 0, 0.6)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsImageModalOpen(false);
              closeModal();
            }}
          >
            <X size={20} />
          </button>
          <img
            src={selectedPost.photo_url}
            alt="Post content"
            className="image-modal-content"
            style={isPhoneFrameMode ? {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            } : undefined}
          />
        </div>
      )}
    </>
  );
}
